import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  cancelDeclaredPredicateSafety,
  dispatchDossierRun,
  getCombinedDeviceDossier,
  getDossierDispatch,
  getDossierRun,
  lookupComparatorUdi,
  lookupPredicateK,
  type CombinedDeviceDossierKind,
  type CombinedEuParts,
  type ComparatorLookup,
  type DossierDispatchArgs,
  type DossierDispatchDetailResponse,
  type DossierDispatchResponse,
  type DossierRunDetail,
  type PredicateLookup,
} from './api';

import { EU_UDI_RE, K_NUMBER_RE } from './predicate-k';

/** Reflect-back lookup for a user-typed predicate K-number. Fires only once
 *  the value is a complete, well-formed K (K + 6 digits); a clearance record
 *  is immutable, so results cache long and never refetch on focus. `retry:
 *  false` — a 503 means openFDA is down and the number will be verified at
 *  generate anyway; don't hammer. */
export function usePredicateKLookup(productId: number, k: string) {
  const normalized = k.trim().toUpperCase();
  return useQuery<PredicateLookup>({
    queryKey: ['predicate-k-lookup', productId, normalized],
    queryFn: () => lookupPredicateK(productId, normalized),
    enabled: K_NUMBER_RE.test(normalized),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** EU twin of usePredicateKLookup. `retry: false` — an unreachable EUDAMED
 *  degrades to "verified at generate" rather than hammering it. */
export function useComparatorUdiLookup(productId: number, udi: string) {
  const normalized = udi.trim().toUpperCase();
  return useQuery<ComparatorLookup>({
    queryKey: ['comparator-udi-lookup', productId, normalized],
    queryFn: () => lookupComparatorUdi(productId, normalized),
    enabled: EU_UDI_RE.test(normalized),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useDispatchDossierRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: DossierDispatchArgs): Promise<DossierDispatchResponse> =>
      dispatchDossierRun(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDossierRun(runId: number | null) {
  const queryClient = useQueryClient();
  const query = useQuery<DossierRunDetail>({
    queryKey: ['dossier-run', runId],
    queryFn: () => {
      if (runId === null) throw new Error('runId is null');
      return getDossierRun(runId);
    },
    enabled: runId !== null,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 3000;
      if (data.status === 'completed' || data.status === 'failed') return false;
      return data.poll_ms || 3000;
    },
    refetchIntervalInBackground: false,
    // 'always', NOT true: a focus-triggered refetch is otherwise gated by the
    // global 5-min staleTime, so a run that completes while the tab is
    // backgrounded (interval polling paused) stays "running" on screen until a
    // hard reload. 'always' makes returning to the tab always re-check.
    refetchOnWindowFocus: 'always',
    // Re-check on OPEN too — navigating in from the table's "View" reuses a
    // cached "running" snapshot (< 5-min-stale), so without this the page
    // shows steppers until a manual refresh.
    refetchOnMount: 'always',
    retry: 1,
  });

  const terminalStatus =
    query.data && (query.data.status === 'completed' || query.data.status === 'failed')
      ? query.data.status
      : null;
  useEffect(() => {
    if (terminalStatus) {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  }, [terminalStatus, queryClient]);

  return query;
}

/** Poll a dossier dispatch group for status of every sibling country
 *  run. Stops polling when all children reach a terminal state.
 *  Polls every 5s (longer than predicate's 3s — dossier runs are minutes). */
export function useDossierDispatchDetail(groupId: string | null) {
  return useQuery<DossierDispatchDetailResponse>({
    queryKey: ['dossier-dispatch', groupId],
    queryFn: () => {
      if (groupId === null) throw new Error('groupId is null');
      return getDossierDispatch(groupId);
    },
    enabled: groupId !== null,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 5000;
      const allTerminal = data.runs.every(
        (r) => r.status === 'completed' || r.status === 'failed',
      );
      return allTerminal ? false : 5000;
    },
    refetchIntervalInBackground: false,
    // Refetch when the user returns to the tab — otherwise, if they tab away
    // while the last country/annex finishes (background polling is paused), the
    // per-country tabs stay stuck on "in progress" until a hard refresh.
    // 'always' (not true) so the refetch isn't silently gated by the global
    // 5-min staleTime when they refocus soon after backgrounding.
    refetchOnWindowFocus: 'always',
    // Re-check on OPEN too — navigating in from the table's "View" reuses a
    // cached "running" snapshot (< 5-min-stale), so without this the page
    // shows steppers until a manual refresh.
    refetchOnMount: 'always',
    retry: 1,
  });
}

/** A complete device dossier's parts (EU MDR / EU IVDR / NI MDR / NI IVDR) for the
 *  complete-dossier page, pinned to ONE dispatch by ``runId`` (any of its runs; null = the latest
 *  completed dispatch). Fetched fresh every time the page opens, and ``parts`` stays null (the
 *  loading state) until THIS open's fetch is back — a cached copy from the last visit (a Part
 *  still "not yet generated") is never drawn first. The run id is in the key, so two dispatches
 *  of one product never share a cache entry. Never refetched on window focus; the app's standard
 *  retry rule (lib/query-client) decides retries. */
export function useCombinedDeviceDossier(
  kind: CombinedDeviceDossierKind,
  productId: number,
  deviceClass: string,
  runId: number | null,
): { parts: CombinedEuParts | null; failed: boolean } {
  const query = useQuery<CombinedEuParts>({
    queryKey: ['combined-device-dossier', kind, productId, deviceClass, runId],
    queryFn: () => getCombinedDeviceDossier(kind, productId, deviceClass, runId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  return {
    parts: query.isFetchedAfterMount ? (query.data ?? null) : null,
    failed: query.isError,
  };
}

/** Log a Cancel on the MAUDE/recall warning for a typed-in K-number. */
export function useCancelDeclaredPredicateSafety() {
  return useMutation({
    mutationFn: (v: { productId: number; kNumber: string }) =>
      cancelDeclaredPredicateSafety(v.productId, v.kNumber),
    onError: () => {
      toast.error('Could not record your choice in the audit log.');
    },
  });
}
